import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  InstallReport,
  ReportDataRow,
} from "api/interfaces/reportInterface.interface";
import {
  deleteReport,
  postInstallReportCreate,
  putInstallReportCreate,
} from "api/reportAPI";
import { notify } from "components/atoms/notification/Notification";
import { useAuth } from "components/Auth";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "redux/hooks";
import AddInstallPresenter from "./AddInstallPresenter";
import { SelectedRowWithAddress } from "components/element/search/SearchRecorderList";
import { isEmpty, merge } from "lodash";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { AddReportProps } from "../AddReport";
import useStatusMutation from "hook/useStatusMutation";
import { StepType } from "components/blocks/stepper/Stepper";
import { getTodayToUTCDate } from "utils/timeUtil";
import { getUserProfile } from "api/userAPI";
import { useQuery, useQueryClient } from "react-query";
import { GetUserDetailResponse } from "api/interfaces/userInterface.interface";
import { checkEmpty, toTrim } from "utils/functions";

export enum UPDATE_TYPE {
  RECORDER,
  RECORDER_GROUP,
  CUSTOMER_NAME,
  CUSTOMER_PHONE,
  CUSTOMER_ADDRESS,
  INSTALLER_NAME,
  INSTALLER_PHONE,
  COMPANY_NAME,
  COMPANY_PHONE,
  COMPANY_ADDRESS,
  INSTALL_DATE,
  DEVICE_DESC,
  ACCOUNT,
  TITLE,
}

export enum UPDATE_ADDRESS_TYPE {
  CUSTOMER,
  INSTALLER,
}

type Props = {
  reportRow?: ReportDataRow;
  data?: InstallReport;
} & AddReportProps;

const initialReportState: InstallReport = {
  title: "",
  targetAccountId: "",
  recorder: {
    recorderId: "",
  },
  information: {
    customerName: "",
    customerPhoneNumber: "",
    customerLocation: "",
    installerName: "",
    installerPhoneNumber: "",
    installerCompany: "",
    installerCompanyPhoneNumber: "",
    installerCompanyAddress: "",
    installationDate: "",
    deviceSerial: "",
    deviceDescription: "",
    analogCameraModels: [],
  },
};

export default function AddInstallContainer(props: Props): JSX.Element {
  const auth = useAuth();
  const { lang, intlString, notifySuccess, handleErrorNotify } = useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [page, setPage] = useState<number>(0);

  // 나라 선택
  const [isFlagSelected, setIsFlagSelected] = useState<string>("US");

  const [installationReport, setInstallationReport] = useState<InstallReport>({
    ...initialReportState,
  });

  const getInstallerInfo = useQuery(
    ["profile", selectedAccount.accountId, auth.user.userId],
    () =>
      getUserProfile({
        accountId: selectedAccount.accountId,
        userId: auth.user.userId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetUserDetailResponse) => {
        if (res.error === 0 && res.result !== undefined && !props.isEdit) {
          setInstallationReport((info) => {
            return {
              ...info,
              information: {
                ...info.information,
                installerName: res.result.firstName + " " + res.result.lastName,
                installerPhoneNumber: res.result.phone,
                installerCompany: res.result.companyName,
                installerCompanyAddress: res.result.companyAddress,
                installerCompanyPhoneNumber: res.result.companyPhoneNumber,
              },
            };
          });
        }
      },
    }
  );
  useEffect(() => {
    if (props.data !== undefined) {
      const newObj = merge({}, initialReportState, props.data);
      setInstallationReport(newObj);
      if (props.isEdit) {
        setPage(2);
      }
    }
  }, [props.data]);

  const steps: StepType[] = [
    {
      label: "Organization",
    },
    {
      label: "Recorder",
    },
    {
      label: "Information",
    },
  ];

  const onChangePage = (name: string, index?: number) => {
    setReportRuleValidate((info) => {
      return { ...info, isRecorder: false as boolean };
    });
    setReportRuleValidateMsg((info) => {
      return { ...info, isRecorder: "" };
    });
    if (
      name !== "previous" &&
      page === 0 &&
      !props.isEdit &&
      !installationReport.targetAccountId
    ) {
      setReportRuleValidate((info) => {
        return { ...info, isRecorder: true as boolean };
      });

      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isRecorder: intlString(
            lang.validateMsg.report.rule.emptySingleAccount
          ),
        };
      });
      setPage(0);
      return;
    }
    if (
      !(name === "previous" || (name === "step" && index === 0)) &&
      page === 1 &&
      !props.isEdit &&
      (installationReport.recorder.recorderId === undefined ||
        installationReport.recorder.recorderId.length === 0)
    ) {
      setReportRuleValidate((info) => {
        return { ...info, isRecorder: true as boolean };
      });

      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isRecorder: intlString(
            lang.validateMsg.report.rule.emptySingleRecorder
          ),
        };
      });
      setPage(1);
      return;
    }
    if (installationReport.information.installationDate === "") {
      const date = getTodayToUTCDate(selectedAccount.timezone);
      setInstallationReport((info) => {
        return {
          ...info,
          information: {
            ...info.information,
            installationDate: date,
          },
        };
      });
    }
    if (name === "next") {
      setPage(page + 1);
    }
    if (name === "previous") {
      setPage(page - 1);
    }
    if (name === "save") {
      props.onModal();
    }
    if (name === "step" && index !== undefined) {
      setPage(index);
    }
  };

  // 나라 선택 함수
  const onSelectFlag = (code: string) => {
    setIsFlagSelected(code);
  };

  const mutationCreateReport = useStatusMutation(postInstallReportCreate, {
    onSuccess: () => {
      notifySuccess(lang.label.report.notify.install.create.success);
      props.onModal();
      queryClient.invalidateQueries("reportList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.report.notify.install.create.fail);
    },
  });

  const mutationUpdateReport = useStatusMutation(putInstallReportCreate, {
    onSuccess: () => {
      notifySuccess(lang.label.report.notify.install.update.success);
      props.onModal();
      queryClient.invalidateQueries("reportList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.report.notify.install.update.fail);
    },
  });

  const [reportRuleValidate, setReportRuleValidate] = useState<Validate>({
    isRecorder: false,
    isCustomName: false,
    isCustomPhone: false,
    isCustomAddress: false,
    isInstallerName: false,
    isInstallerPhone: false,
    isTitle: false,
  });
  const [reportRuleValidateMsg, setReportRuleValidateMsg] =
    useState<ValidateMsg>({
      isRecorder: "",
      isCustomName: "",
      isCustomPhone: "",
      isCustomAddress: "",
      isInstallerName: "",
      isInstallerPhone: "",
      isTitle: "",
    });

  const initReportRuleValidate = useCallback((field: string) => {
    setReportRuleValidate((info) => {
      return { ...info, [field as keyof typeof reportRuleValidate]: false };
    });
    setReportRuleValidateMsg((info) => {
      return { ...info, [field as keyof typeof reportRuleValidateMsg]: "" };
    });
  }, []);

  const onClickCreate = useCallback(() => {
    //setIsAddModal(true);
    initReportRuleValidate("isRecorder");
    initReportRuleValidate("isCustomName");
    initReportRuleValidate("isCustomPhone");
    initReportRuleValidate("isCustomAddress");
    initReportRuleValidate("isInstallerName");
    initReportRuleValidate("isInstallerPhone");
    initReportRuleValidate("isTitle");
    const payload = {
      ...installationReport,
      information: {
        ...installationReport.information,
        customerName: toTrim(installationReport.information.customerName),
        customerPhoneNumber: toTrim(
          installationReport.information.customerPhoneNumber
        ),
        customerLocation: toTrim(
          installationReport.information.customerLocation
        ),
        installerName: toTrim(installationReport.information.installerName),
        installerPhoneNumber: toTrim(
          installationReport.information.installerPhoneNumber
        ),
        installerCompany: toTrim(
          installationReport.information.installerCompany
        ),
        installerCompanyPhoneNumber: toTrim(
          installationReport.information.installerCompanyPhoneNumber
        ),
        installerCompanyAddress: toTrim(
          installationReport.information.installerCompanyAddress
        ),
        deviceDescription: toTrim(
          installationReport.information.deviceDescription
        ),
      },
      targetAccountId: installationReport.targetAccountId,
    };
    let isError = false;

    if (checkEmpty(payload.title)) {
      setReportRuleValidate((info) => {
        return { ...info, isTitle: true as boolean };
      });
      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isTitle: intlString(lang.validateMsg.report.rule.emptyTitle),
        };
      });
      isError = true;
    }

    if (checkEmpty(payload.information.customerName)) {
      setReportRuleValidate((info) => {
        return { ...info, isCustomName: true as boolean };
      });

      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isCustomName: intlString(
            lang.validateMsg.report.rule.emptyCustomName
          ),
        };
      });
      setInstallationReport((info) => {
        return {
          ...info,
          information: { ...info.information, customerName: "" },
        };
      });
      isError = true;
    }
    if (checkEmpty(payload.information.customerPhoneNumber)) {
      setReportRuleValidate((info) => {
        return { ...info, isCustomPhone: true as boolean };
      });

      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isCustomPhone: intlString(
            lang.validateMsg.report.rule.emptyCustomPhone
          ),
        };
      });
      setInstallationReport((info) => {
        return {
          ...info,
          information: { ...info.information, customerPhoneNumber: "" },
        };
      });
      isError = true;
    }

    if (checkEmpty(payload.information.customerLocation)) {
      setReportRuleValidate((info) => {
        return { ...info, isCustomAddress: true as boolean };
      });

      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isCustomAddress: intlString(
            lang.validateMsg.report.rule.emptyCustomAddress
          ),
        };
      });
      setInstallationReport((info) => {
        return {
          ...info,
          information: { ...info.information, customerPhoneNumber: "" },
        };
      });
      isError = true;
    }

    if (checkEmpty(payload.information.installerName)) {
      setReportRuleValidate((info) => {
        return { ...info, isInstallerName: true as boolean };
      });

      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isInstallerName: intlString(
            lang.validateMsg.report.rule.emptyInstallName
          ),
        };
      });
      setInstallationReport((info) => {
        return {
          ...info,
          information: { ...info.information, installerName: "" },
        };
      });
      isError = true;
    }

    if (checkEmpty(payload.information.installerPhoneNumber)) {
      setReportRuleValidate((info) => {
        return { ...info, isInstallerPhone: true as boolean };
      });

      setReportRuleValidateMsg((info) => {
        return {
          ...info,
          isInstallerPhone: intlString(
            lang.validateMsg.report.rule.emptyInstallPhone
          ),
        };
      });
      setInstallationReport((info) => {
        return {
          ...info,
          information: { ...info.information, installerPhoneNumber: "" },
        };
      });
      isError = true;
    }

    if (isError) {
      setPage(2);
      return;
    }

    if (
      !mutationCreateReport.idle ||
      !mutationUpdateReport.idle ||
      !mutationDeleteReport.idle
    )
      return;
    if (props.isEdit) {
      mutationUpdateReport.mutate({
        payload: payload,
        reportId: props.reportRow?.id as string,
      });
    } else {
      mutationCreateReport.mutate({
        payload: payload,
        accountId: selectedAccount.accountId,
      });
    }
  }, [installationReport, mutationCreateReport, mutationUpdateReport]);

  const onChangeInstallReportInfo = useCallback(
    (
      type: UPDATE_TYPE,
      value:
        | string
        | number
        | boolean
        | string[]
        | boolean[]
        | Date
        | SelectedRowWithAddress
    ) => {
      if (type === UPDATE_TYPE.TITLE) {
        setInstallationReport((info) => {
          return {
            ...info,
            title: value as string,
          };
        });
      }
      if (type === UPDATE_TYPE.ACCOUNT) {
        let id = (value as string[])?.[0] ?? undefined;
        if (id) {
          id = id.replace("_EU", "");
        }
        setInstallationReport((info) => {
          return {
            ...info,
            targetAccountId: id,
          };
        });
      }
      if (type === UPDATE_TYPE.RECORDER) {
        setInstallationReport((info) => {
          return {
            ...info,
            recorder: {
              ...info.recorder,
              recorderId: (value as SelectedRowWithAddress).id,
            },
          };
        });

        if (!props.isEdit) {
          setInstallationReport((info) => {
            return {
              ...info,
              information: {
                ...info.information,
                customerLocation: (value as SelectedRowWithAddress).address,
              },
            };
          });
        }
      } else if (type === UPDATE_TYPE.CUSTOMER_NAME) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: { ...info.information, customerName: value as string },
          };
        });
      } else if (type === UPDATE_TYPE.CUSTOMER_PHONE) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              customerPhoneNumber: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.CUSTOMER_ADDRESS) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              customerLocation: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.INSTALLER_NAME) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              installerName: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.INSTALLER_PHONE) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              installerPhoneNumber: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.COMPANY_NAME) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              installerCompany: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.COMPANY_PHONE) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              installerCompanyPhoneNumber: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.COMPANY_ADDRESS) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              installerCompanyAddress: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.INSTALL_DATE) {
        //BUG [RND-409] calendar 선택
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              installationDate: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DEVICE_DESC) {
        setInstallationReport((info) => {
          return {
            ...info,
            information: {
              ...info.information,
              deviceDescription: value as string,
            },
          };
        });
      }
    },
    []
  );

  const onChangeAddress = (type: UPDATE_ADDRESS_TYPE, address: string) => {
    if (type === UPDATE_ADDRESS_TYPE.CUSTOMER) {
      setInstallationReport((info) => {
        return {
          ...info,
          information: {
            ...info.information,
            customerLocation: address as string,
          },
        };
      });
    } else {
      setInstallationReport((info) => {
        return {
          ...info,
          information: {
            ...info.information,
            installerCompanyAddress: address as string,
          },
        };
      });
    }
  };

  const [isConfirm, setIsConfirm] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const mutationDeleteReport = useStatusMutation(deleteReport, {
    onSuccess: () => {
      notify("success", lang.label.report.notify.install.delete.success);
      props.onModal("edit");
      queryClient.invalidateQueries("reportList");
      props.onSuccessDelete && props.onSuccessDelete();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.report.notify.install.delete.fail);
    },
  });

  const onConfirmDelete = () => {
    mutationDeleteReport.mutate({
      reportId: props.reportRow?.id as string,
      accountId: selectedAccount.accountId,
    });
  };
  const setIsDeleteConfirm = (confirm: boolean) => {
    setIsConfirm(confirm);
  };
  const onDelete = (value: boolean) => {
    setIsConfirm(value);
  };

  return (
    <AddInstallPresenter
      isEdit={props.isEdit}
      steps={steps}
      page={page}
      userInfo={auth.user}
      installationReport={installationReport}
      isFlagSelected={isFlagSelected}
      accountId={selectedAccount.accountId}
      onChangePage={onChangePage}
      onSelectFlag={onSelectFlag}
      onChangeReportInfo={onChangeInstallReportInfo}
      onClickCreate={onClickCreate}
      onChangeAddress={onChangeAddress}
      onConfirmDelete={onConfirmDelete}
      onDelete={onDelete}
      setIsDeleteConfirm={setIsDeleteConfirm}
      isConfirm={isConfirm}
      reportRuleValidate={reportRuleValidate}
      reportRuleValidateMsg={reportRuleValidateMsg}
      reportRow={props.reportRow}
      saveIsInProgress={
        !mutationCreateReport.idle || !mutationUpdateReport.idle
      }
      deleteIsInProgress={!mutationDeleteReport.idle}
      selectedAccount={selectedAccount}
      targetAccountId={installationReport.targetAccountId}
    />
  );
}
